// src/features/owner/sections/analytics/RetentionSection.tsx — real D1/D7 signup
// cohorts (1.24.00 F2).
//
// This was a three-line wrapper over the shared `AnalyticsSection` archetype
// until the backend started sending a `cohorts` array on the retention kind
// only. The KPI strip, the range picker and the trend chart are still the
// archetype's — deliberately, so this page keeps reading like its four siblings
// — but the cohort table underneath is why the section exists, and no shared
// component can render it: it is the one place in the console where a missing
// number and a zero mean opposite things, and the difference has to survive all
// the way to the pixel.
//
// The archetype itself is NOT extended for this. Overview, User Statistics,
// Engagement and Growth all mount `AnalyticsSection`, and none of them ever
// receives a `cohorts` key; teaching the shared component about a field only
// one of its five callers can have would make the other four carry a branch
// that is dead by construction.
import { useState } from 'react';
import styled from 'styled-components';
import { useOwnerAnalytics } from '@/hooks';
import { KpiCard, OwnerPanel, EmptyState, DateRangePicker, DataTable } from '../../components';
import type { Column } from '../../components';
import { ComboChart } from '../../charts';
import type { RetentionCohort } from '@/core/models';

const Strip = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 10px;
  margin-bottom: 12px;
`;

const PickerRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
`;

// Copied from the archetype rather than shared: `AnalyticsSection` is mounted by
// the other four sections and stays untouched, and one styled-component is a
// cheaper duplicate than a new export surface between the two.
const Stat = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 7px 0;
  border-top: 1px solid ${({ theme }) => theme.color.glassBorder};
  &:first-child { border-top: none; }
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;

const Note = styled.p`
  margin: 0 0 12px;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  max-width: 70ch;
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const TONES = ['brand', 'blue', 'mint', 'ember', 'magenta', 'teal'];

/**
 * A cohort cell: `67% (2)`, or an em-dash when the server sent null.
 *
 * Null means the day has not arrived yet, not that nobody came back — see the
 * `RetentionCohort` doc comment. Falling back to `0` here (`pct ?? 0`) is the
 * single mutation this whole section exists to prevent: it would draw a
 * confident 0% retention line for cohorts whose seventh day is still in the
 * future, and the owner would read a cliff that is really a calendar.
 *
 * The percentage is printed exactly as the server sent it. It is already an
 * integer rounded against `joined`, and re-deriving it from the count here
 * would let the console disagree with the API over rounding.
 */
function cell(pct: number | null, returned: number | null): string {
  if (pct === null || returned === null) return 'Not yet';
  return `${pct}% (${returned})`;
}

export function RetentionSection() {
  const [rangeDays, setRangeDays] = useState(7);
  const { data, loading, error } = useOwnerAnalytics('retention', rangeDays);

  if (loading) return <p style={{ opacity: 0.6 }}>Loading…</p>;
  if (error || !data) return <EmptyState icon="bar-chart-3" message={error ?? 'No data'} />;

  // Absent on an older server, and absent on every non-retention kind — so the
  // table's empty state is the normal path here, not an error path.
  const cohorts: RetentionCohort[] = data.cohorts ?? [];

  const columns: Column<RetentionCohort>[] = [
    {
      key: 'weekOf',
      header: 'Week of',
      sortBy: (r) => r.weekOf,
      render: (r) => r.weekOf,
    },
    {
      key: 'joined',
      header: 'Joined',
      sortBy: (r) => r.joined,
      render: (r) => String(r.joined),
    },
    {
      key: 'd1',
      header: 'Came back next day',
      render: (r) => cell(r.d1Pct, r.d1Returned),
    },
    {
      key: 'd7',
      header: 'Came back day 7',
      render: (r) => cell(r.d7Pct, r.d7Returned),
    },
  ];

  return (
    <>
      <PickerRow>
        <DateRangePicker valueDays={rangeDays} onChange={setRangeDays} />
      </PickerRow>
      <Strip>
        {data.kpis.map((k, i) => (
          <KpiCard
            key={k.key}
            icon="bar-chart-3"
            tone={TONES[i % TONES.length] ?? 'brand'}
            label={k.label}
            value={k.value}
            deltaPct={k.deltaPct}
            trend={k.trend}
          />
        ))}
      </Strip>
      <OwnerPanel title="Trend">
        <ComboChart points={data.series} ariaLabel="retention trend" />
      </OwnerPanel>
      <div style={{ height: 10 }} />
      <OwnerPanel title="Weekly cohorts">
        <Stack>
          {/* The horizon is fixed in the data, not in the picker above it, and
              saying so out loud is the only thing standing between the owner and
              a wrong conclusion — "Last 90 days" is selected, four rows are on
              screen, and nothing on the page explains the gap. */}
          <Note data-testid="retention-horizon-note">
            This table always covers the last 30 days, whatever the range above is set to. The
            &ldquo;saw something&rdquo; signal it counts is only kept for 30 days, so older cohorts
            are left out rather than guessed at. The range picker still drives the numbers and the
            chart at the top of this page. &ldquo;Came back next day&rdquo; means the exact next
            calendar day, and &ldquo;came back day 7&rdquo; the exact seventh day. Neither one
            means &ldquo;some time within a week&rdquo;. &ldquo;Not yet&rdquo; is a day that has not
            happened yet for that cohort, which is not the same as nobody coming back.
          </Note>
          <DataTable<RetentionCohort>
            columns={columns}
            rows={cohorts}
            rowKey={(r) => r.weekOf}
            emptyIcon="calendar-clock"
            emptyMessage="No cohorts in the last 30 days yet"
          />
        </Stack>
      </OwnerPanel>
      <div style={{ height: 10 }} />
      {/* The archetype's breakdown, kept: the server still sends `table` for the
          retention kind, and this page showed it before the cohorts existed. */}
      <OwnerPanel title="Breakdown">
        {data.table.length === 0
          ? <EmptyState icon="activity" message="Not enough data yet" />
          : data.table.map((r) => (
            <Stat key={r.label}>
              <span>{r.label}</span>
              <b>{r.value}</b>
            </Stat>
          ))}
      </OwnerPanel>
    </>
  );
}
